import { useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import MainContainer from "../../../layout/MainContainer";
import "../Users.scss";
import {
  useChangeUserStatusMutation,
  useLazyCancelUserSubscriptionQuery,
  useLazyGetUserByIdQuery,
  useNudgeUserMutation,
} from "../../../services/customers";
import { AllUser } from "../../../types/General";
import { Loader, showError, showToast } from "../../../constants";
import useAuth from "../../../hooks/useAuth";
import { Permissions } from "../../../types/User";
import moment from "moment";

type UserProfileData = AllUser & {
  customerStats?: {
    bankAccountsLinked?: number;
    budgetsCreated?: number;
    subscriptionsDetected?: number;
    billsDetected?: number;
  };
  planDetails?: {
    planName?: string;
    validity?: number | string;
    price?: number;
    isRecurring?: boolean;
    subscriptionEndDate?: string | null;
  };
  setupInfo?: {
    platform?: string;
    registrationMethod?: string;
    deviceType?: string | null;
    createdAt?: string | null;
  };
  associatedData?: {
    bankAccounts?: Array<{
      _id?: string;
      name?: string;
      institutionName?: string;
      mask?: string;
      subtype?: string;
      createdAt?: string | null;
    }>;
    budgets?: Array<{
      _id?: string;
      name?: string;
      amount?: number;
      spent?: number;
      remaining?: number;
      frequency?: string;
      createdAt?: string | null;
    }>;
    subscriptions?: Array<{
      _id?: string;
      name?: string;
      amount?: number;
      date?: string | null;
      merchantName?: string;
    }>;
    bills?: Array<{
      _id?: string;
      name?: string;
      amount?: number;
      date?: string | null;
      merchantName?: string;
    }>;
    recentTransactions?: Array<{
      _id?: string;
      name?: string;
      amount?: number;
      date?: string | null;
      merchantName?: string;
    }>;
  };
};

const HEX_ONLY = /^[0-9a-fA-F]+$/;

const looksLikeEncryptedPayload = (value: string) => {
  const raw = String(value || "").trim();
  if (!raw) return false;
  const parts = raw.split(":");
  if (parts.length !== 2) return false;
  const [ivHex, encryptedHex] = parts;
  if (!ivHex || !encryptedHex) return false;
  if (ivHex.length !== 32 || encryptedHex.length < 32) return false;
  return HEX_ONLY.test(ivHex) && HEX_ONLY.test(encryptedHex);
};

const looksLikeSensitiveHash = (value: string) => {
  const raw = String(value || "").trim();
  if (raw.length < 32) return false;
  return HEX_ONLY.test(raw);
};

const sanitizeCustomerValue = (
  value: string | null | undefined,
  type: "name" | "email" | "phone"
) => {
  const raw = String(value || "").trim();
  if (!raw) return "";

  if (type === "email") {
    const emailLike = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw);
    if (emailLike) return raw;
    if (looksLikeEncryptedPayload(raw) || looksLikeSensitiveHash(raw)) return "";
    return raw;
  }

  if (type === "phone") {
    const digits = raw.replace(/\D/g, "");
    if (digits.length >= 7 && digits.length <= 15) return raw;
    if (looksLikeEncryptedPayload(raw) || looksLikeSensitiveHash(raw)) return "";
    return raw;
  }

  if (looksLikeEncryptedPayload(raw) || looksLikeSensitiveHash(raw)) return "";
  return raw;
};

const UserDetails = () => {
  const authUser = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [userData, setUserData] = useState<UserProfileData>();
  const [getUserById, { isLoading }] = useLazyGetUserByIdQuery();
  const [changeStatus, statusState] = useChangeUserStatusMutation();
  const [cancelSubscription, cancelSubscriptionState] =
    useLazyCancelUserSubscriptionQuery();
  const [nudgeUser, nudgeState] = useNudgeUserMutation();
  const [nudgeMessage, setNudgeMessage] = useState<string>("");

  const userPermissions = authUser?.permissions?.length
    ? JSON.parse(`${authUser?.permissions}`)
    : [];

  const getUserDetail = useCallback(async (userId: string) => {
    try {
      const response = await getUserById({
        user_id: userId,
      }).unwrap();
      if (response?.statusCode === 200) {
        setUserData(response?.data as UserProfileData);
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  }, [getUserById]);

  const checkPermission = () => {
    const permission = userPermissions;
    if (permission?.length) {
      let idx = -1;
      idx = permission?.findIndex(
        (ele: Permissions) => ele?.label === "Manage Customers"
      );
      if (idx > -1) {
        const manageViewPermission = permission[idx];
        if (manageViewPermission && !manageViewPermission?.isView) {
          navigate("/permission-denied", { replace: true });
        }
      } else {
        navigate("/permission-denied", { replace: true });
      }
    }
  };

  const updateStatus = async () => {
    if (!userData?._id) return;
    try {
      const response = await changeStatus({
        user_id: userData._id,
        body: {
          isBlocked: !userData?.isBlocked,
        },
      }).unwrap();
      if (response?.statusCode === 200) {
        showToast(response?.message || "Status updated");
        getUserDetail(userData._id);
      }
    } catch (error: any) {
      showError(error?.data?.message || "Unable to update user status");
    }
  };

  const handleCancelSubscription = async () => {
    if (!userData?._id) return;
    try {
      const response = await cancelSubscription({ user_id: userData._id }).unwrap();
      if (response?.statusCode === 200) {
        showToast(response?.message || "Subscription cancelled");
        getUserDetail(userData._id);
      }
    } catch (error: any) {
      showError(error?.data?.message || "Unable to cancel subscription");
    }
  };

  const handleSendNudge = async () => {
    if (!userData?._id) return;
    try {
      const response = await nudgeUser({
        user_id: userData._id,
        body: {
          message: nudgeMessage.trim() || undefined,
          channels: ["push", "email"],
        },
      }).unwrap();
      if (response?.statusCode === 200) {
        const emailStatus = response?.data?.nudge?.delivery?.email?.sent
          ? "email sent"
          : "email skipped";
        const pushStatus = response?.data?.nudge?.delivery?.push?.queued
          ? "push queued"
          : "push skipped";
        showToast(response?.message || `Nudge delivered (${pushStatus}, ${emailStatus})`);
        setNudgeMessage("");
      }
    } catch (error: any) {
      showError(error?.data?.message || "Unable to nudge user");
    }
  };

  useEffect(() => {
    if (authUser && userPermissions?.length) {
      checkPermission();
    }
  }, [authUser, userPermissions, navigate]);

  useEffect(() => {
    if (id) {
      getUserDetail(id);
    }
  }, [id, getUserDetail]);

  const planName = useMemo(() => {
    const raw = userData?.planDetails?.planName || (userData as any)?.subscription || "";
    const normalized = String(raw).toLowerCase();
    if (!normalized) return "Free";
    if (normalized.includes("lite")) return "SaverLite";
    if (normalized.includes("pro")) return "SaverPro";
    if (normalized.includes("trial")) return "Trial";
    return String(raw);
  }, [userData]);

  const safeFirstName = useMemo(
    () => sanitizeCustomerValue(userData?.firstName, "name"),
    [userData?.firstName]
  );
  const safeLastName = useMemo(
    () => sanitizeCustomerValue(userData?.lastName, "name"),
    [userData?.lastName]
  );
  const safeEmail = useMemo(
    () => sanitizeCustomerValue(userData?.email, "email"),
    [userData?.email]
  );
  const safePhone = useMemo(
    () => sanitizeCustomerValue(userData?.phone, "phone"),
    [userData?.phone]
  );
  const linkedBankAccounts = userData?.associatedData?.bankAccounts || [];
  const budgets = userData?.associatedData?.budgets || [];
  const subscriptions = userData?.associatedData?.subscriptions || [];
  const bills = userData?.associatedData?.bills || [];
  const recentTransactions = userData?.associatedData?.recentTransactions || [];

  return (
    <MainContainer>
      <Loader
        isLoad={
          isLoading ||
          statusState?.isLoading ||
          cancelSubscriptionState?.isLoading ||
          nudgeState?.isLoading
        }
      />
      <div className="main_loyout">
        <div className="dashboard">
          <h1 className="mn_hdng">Customer Profile</h1>
          <Button className="btn btn_primary" onClick={() => navigate("/manageUsers")}>
            Back
          </Button>
        </div>

        <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
          <Button variant="contained" color={userData?.isBlocked ? "success" : "warning"} onClick={updateStatus}>
            {userData?.isBlocked ? "Enable Account" : "Disable Account"}
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={handleCancelSubscription}
            disabled={!userData?._id}
          >
            Cancel Subscription
          </Button>
          <Chip
            label={userData?.isBlocked ? "Blocked" : "Active"}
            color={userData?.isBlocked ? "error" : "success"}
            variant="outlined"
          />
        </Stack>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mb: 2 }}>
          <TextField
            size="small"
            fullWidth
            label="Custom nudge message (optional)"
            placeholder="Example: Complete your setup by linking your first bank account."
            value={nudgeMessage}
            onChange={(event) => setNudgeMessage(event.target.value)}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleSendNudge}
            disabled={!userData?._id}
            sx={{ minWidth: 180 }}
          >
            Send Nudge
          </Button>
        </Stack>

        <Card className="cards" sx={{ border: "1px solid #e5e7eb" }}>
          <CardContent sx={{ p: 2 }}>
            <Grid container spacing={2} className="view_box">
              <Grid item lg={3} md={4} sm={6} xs={12}>
                <Typography component="h5">Name</Typography>
                <Typography component="p">
                  {`${safeFirstName || "-"} ${safeLastName || ""}`.trim()}
                </Typography>
              </Grid>
              <Grid item lg={3} md={4} sm={6} xs={12}>
                <Typography component="h5">Email</Typography>
                <Typography component="p">{safeEmail || "-"}</Typography>
              </Grid>
              <Grid item lg={3} md={4} sm={6} xs={12}>
                <Typography component="h5">Phone</Typography>
                <Typography component="p">
                  {safePhone && userData?.countryCode
                    ? (userData?.countryCode.includes("+") ? "" : "+") + userData?.countryCode
                    : ""}{" "}
                  {safePhone || "-"}
                </Typography>
              </Grid>
              <Grid item lg={3} md={4} sm={6} xs={12}>
                <Typography component="h5">Joined On</Typography>
                <Typography component="p">
                  {userData?.createdAt ? moment(userData.createdAt).format("DD MMM YYYY") : "-"}
                </Typography>
              </Grid>
              <Grid item lg={3} md={4} sm={6} xs={12}>
                <Typography component="h5">Platform</Typography>
                <Typography component="p">{userData?.setupInfo?.platform || "-"}</Typography>
              </Grid>
              <Grid item lg={3} md={4} sm={6} xs={12}>
                <Typography component="h5">Account Setup Method</Typography>
                <Typography component="p">{userData?.setupInfo?.registrationMethod || "-"}</Typography>
              </Grid>
              <Grid item lg={3} md={4} sm={6} xs={12}>
                <Typography component="h5">Device Type</Typography>
                <Typography component="p">{userData?.setupInfo?.deviceType || "-"}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography className="detail_title mn_hdng" component="h2" mb={1}>
                  Plan & Activity Snapshot
                </Typography>
              </Grid>

              <Grid item lg={3} md={4} sm={6} xs={12}>
                <Typography component="h5">Plan Name</Typography>
                <Typography component="p">{planName}</Typography>
              </Grid>
              <Grid item lg={3} md={4} sm={6} xs={12}>
                <Typography component="h5">Plan Price</Typography>
                <Typography component="p">
                  ${Number(userData?.planDetails?.price || 0).toFixed(2)}
                </Typography>
              </Grid>
              <Grid item lg={3} md={4} sm={6} xs={12}>
                <Typography component="h5">Plan Validity</Typography>
                <Typography component="p">{userData?.planDetails?.validity || "-"}</Typography>
              </Grid>
              <Grid item lg={3} md={4} sm={6} xs={12}>
                <Typography component="h5">Subscription Ends</Typography>
                <Typography component="p">
                  {userData?.planDetails?.subscriptionEndDate
                    ? moment(userData.planDetails.subscriptionEndDate).format("DD MMM YYYY")
                    : "-"}
                </Typography>
              </Grid>

              <Grid item lg={3} md={4} sm={6} xs={12}>
                <Typography component="h5">Linked Banks</Typography>
                <Typography component="p">
                  {Number(userData?.customerStats?.bankAccountsLinked || 0)}
                </Typography>
              </Grid>
              <Grid item lg={3} md={4} sm={6} xs={12}>
                <Typography component="h5">Budgets Created</Typography>
                <Typography component="p">
                  {Number(userData?.customerStats?.budgetsCreated || 0)}
                </Typography>
              </Grid>
              <Grid item lg={3} md={4} sm={6} xs={12}>
                <Typography component="h5">Subscriptions Detected</Typography>
                <Typography component="p">
                  {Number(userData?.customerStats?.subscriptionsDetected || 0)}
                </Typography>
              </Grid>
              <Grid item lg={3} md={4} sm={6} xs={12}>
                <Typography component="h5">Bills Detected</Typography>
                <Typography component="p">
                  {Number(userData?.customerStats?.billsDetected || 0)}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography className="detail_title mn_hdng" component="h2" mb={1}>
                  Associated Data
                </Typography>
              </Grid>

              <Grid item lg={6} xs={12}>
                <Typography component="h5">Linked Bank Accounts ({linkedBankAccounts.length})</Typography>
                {linkedBankAccounts.slice(0, 10).map((account) => (
                  <Typography component="p" key={account?._id || account?.name}>
                    {account?.name || "Bank account"}
                    {account?.institutionName ? ` • ${account.institutionName}` : ""}
                    {account?.mask ? ` • ****${account.mask}` : ""}
                  </Typography>
                ))}
                {!linkedBankAccounts.length && <Typography component="p">-</Typography>}
              </Grid>

              <Grid item lg={6} xs={12}>
                <Typography component="h5">Budgets ({budgets.length})</Typography>
                {budgets.slice(0, 10).map((budget) => (
                  <Typography component="p" key={budget?._id || budget?.name}>
                    {budget?.name || "Budget"}
                    {typeof budget?.amount === "number" ? ` • $${budget.amount.toFixed(2)}` : ""}
                    {budget?.frequency ? ` • ${budget.frequency}` : ""}
                  </Typography>
                ))}
                {!budgets.length && <Typography component="p">-</Typography>}
              </Grid>

              <Grid item lg={6} xs={12}>
                <Typography component="h5">Detected Subscriptions ({subscriptions.length})</Typography>
                {subscriptions.slice(0, 10).map((item) => (
                  <Typography component="p" key={item?._id || item?.name}>
                    {item?.name || "Subscription"}
                    {typeof item?.amount === "number" ? ` • $${item.amount.toFixed(2)}` : ""}
                    {item?.date ? ` • ${moment(item.date).format("DD MMM YYYY")}` : ""}
                  </Typography>
                ))}
                {!subscriptions.length && <Typography component="p">-</Typography>}
              </Grid>

              <Grid item lg={6} xs={12}>
                <Typography component="h5">Detected Bills ({bills.length})</Typography>
                {bills.slice(0, 10).map((item) => (
                  <Typography component="p" key={item?._id || item?.name}>
                    {item?.name || "Bill"}
                    {typeof item?.amount === "number" ? ` • $${item.amount.toFixed(2)}` : ""}
                    {item?.date ? ` • ${moment(item.date).format("DD MMM YYYY")}` : ""}
                  </Typography>
                ))}
                {!bills.length && <Typography component="p">-</Typography>}
              </Grid>

              <Grid item xs={12}>
                <Typography component="h5">Recent Transactions ({recentTransactions.length})</Typography>
                {recentTransactions.slice(0, 20).map((item) => (
                  <Typography component="p" key={item?._id || `${item?.name}-${item?.date}`}>
                    {item?.name || "Transaction"}
                    {typeof item?.amount === "number" ? ` • $${item.amount.toFixed(2)}` : ""}
                    {item?.date ? ` • ${moment(item.date).format("DD MMM YYYY")}` : ""}
                  </Typography>
                ))}
                {!recentTransactions.length && <Typography component="p">-</Typography>}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </div>
    </MainContainer>
  );
};

export default UserDetails;
